import {
  ComponentsPlatformService,
  VirtualManifestAPI,
} from '@wix/editor-platform-manifest-services';
import { CompRef } from '@wix/document-services-types';
import { DATA } from '@wix/component-protocol/schema';
import { hasDataType } from '../../../utils/helpers';
import { checkContextMatch } from '../common/actionValidators/helpers';
import { parseFilterValue } from './parser';
import { isAllowedStylePresent } from '../common/actionValidators/actionVisibilityRules';
import { FILTER_CSS_PROPERTY_TYPES } from '../constants';
import { EditorPointer } from '@wix/editor-document-logic-api';

/**
 * Filter action validator
 *
 * Rules:
 * - Must have vectorArt data type
 * - Show if the element has a filter property in its manifest OR has a visible filter value (drop shadow with blur radius)
 *
 * @param componentsAPI - Components platform service
 * @param editorPointers - Selected editor pointers
 * @returns true if filter action should be shown
 */
export const createFilterValidator = () => {
  return (
    componentsAPI: ComponentsPlatformService,
    editorPointers: EditorPointer<CompRef>[],
  ): boolean => {
    const hasVectorArtData = hasDataType(
      componentsAPI.components.getData(editorPointers),
      DATA.DATA_TYPE.vectorArt,
    );

    if (!hasVectorArtData) {
      return false;
    }

    const filterProperties = new Set(FILTER_CSS_PROPERTY_TYPES);

    const rootContexts = editorPointers.map((editorPointer) =>
      componentsAPI.buildManifestAPI({ editorPointers: [editorPointer] }),
    );

    for (const rootContext of rootContexts) {
      if (
        isAllowedStylePresent(rootContext, filterProperties) ||
        checkContextMatch(rootContext, checkForVisibleFilter)
      ) {
        return true;
      }
    }

    return false;
  };
};

const checkForVisibleFilter = (context: VirtualManifestAPI): boolean => {
  const defintions = context.resolver.getStyleDefinitions();
  const styles = context.query.getStyles();

  if (defintions.cssProperties.filter && styles.cssProperties.filter) {
    const parsed = parseFilterValue(styles.cssProperties.filter);

    if (parsed.dropShadow.blurRadius) {
      return true;
    }
  }

  if (defintions.cssCustomProperties && styles.cssCustomProperties) {
    for (const [styleId, value] of Object.entries(styles.cssCustomProperties)) {
      const cssPropertyType =
        defintions.cssCustomProperties[styleId]?.cssPropertyType;

      if (cssPropertyType && cssPropertyType === 'filter' && value) {
        const parsed = parseFilterValue(value);

        if (parsed.dropShadow.blurRadius) {
          return true;
        }
      }
    }
  }

  return false;
};
